import { CheckCircle2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from '@/components/ui/dialog';

import {
  OPEN_RENEW_EVENT,
  dayMonth,
  requestRenewal,
  useAccess
} from './useAccess';

/**
 * The one place a renewal is asked for. Every button -- header pill, phone
 * strip, welcome reminder, end screen -- opens this, so they cannot drift.
 *
 * It sends the request to the team itself instead of opening the visitor's
 * mail app: a mailto link does nothing on a browser without a mail app set up,
 * and some Signals logins have no mailbox to send from. When the request could
 * not be sent the visitor is told so and can try again; "sent" is only ever
 * shown for a request the server confirmed.
 */
export default function RenewDialog() {
  const { access } = useAccess();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onOpen = () => {
      setError(null);
      setOpen(true);
    };
    window.addEventListener(OPEN_RENEW_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_RENEW_EVENT, onOpen);
  }, []);

  if (!access) return null;
  const sentAt = access.renewal_requested_at;
  const until = access.last_day ? dayMonth(access.last_day) : null;

  const send = async () => {
    setSending(true);
    setError(null);
    const res = await requestRenewal(note);
    setSending(false);
    if (res.ok) setNote('');
    else setError(res.message);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-[calc(100vw-24px)] max-w-[420px] rounded-[14px] p-5">
        {sentAt ? (
          <div className="grid gap-2 text-center">
            <CheckCircle2 className="mx-auto size-8 text-[var(--sig-positive)]" />
            <DialogTitle className="text-[16px]">Request sent</DialogTitle>
            <DialogDescription className="text-[13.5px] leading-relaxed">
              We got your renewal request on {dayMonth(sentAt.slice(0, 10))} and
              will be in touch. Nothing else is needed from you.
            </DialogDescription>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mx-auto mt-2 rounded-lg border border-[var(--sig-border-strong)] px-4 py-1.5 text-[13px] font-medium hover:bg-[var(--sig-panel)]"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="grid gap-3">
            <DialogTitle className="text-[16px]">Request a renewal</DialogTitle>
            <DialogDescription className="text-[13.5px] leading-relaxed">
              {access.status === 'ended'
                ? 'Your access has ended. '
                : until
                  ? `Your access runs until ${until}. `
                  : ''}
              We'll send your request to our team with your account details, and
              they'll get back to you.
            </DialogDescription>
            <label className="grid gap-1.5 text-[13px] text-[var(--sig-muted)]">
              Anything we should know? (optional)
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder="Add two more users from the finance team"
                className="w-full resize-none rounded-lg border border-[var(--sig-border)] bg-[var(--sig-surface)] px-3 py-2 text-[14px] text-[var(--sig-ink)] outline-none focus:border-[var(--sig-blue)]"
              />
            </label>
            {error ? (
              <p className="text-[13px] text-[var(--sig-warn-text)]">{error}</p>
            ) : null}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg px-4 py-2 text-[13.5px] text-[var(--sig-muted)] hover:bg-[var(--sig-panel)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={send}
                disabled={sending}
                className="rounded-lg bg-[var(--sig-blue)] px-4 py-2 text-[13.5px] font-medium text-white hover:opacity-90 disabled:opacity-60"
              >
                {sending ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
